'use client';

import { useEffect, useRef, useState } from 'react';
import { Link as LinkIcon, Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { Link } from '@/core/i18n/navigation';
import { Button } from '@/shared/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
const TIKTOK_URL_REGEX = /^https?:\/\/(www\.|vm\.|vt\.)?tiktok\.com\/.+/i;
const TIKTOK_ERROR_KEYS: Record<string, string> = {
  video_not_found: 'tiktok_error_video_not_found',
  video_too_short: 'tiktok_error_video_too_short',
  video_too_large: 'tiktok_error_video_too_large',
  download_failed: 'tiktok_error_download_failed',
  upload_failed: 'tiktok_error_upload_failed',
  request_timeout: 'tiktok_error_request_timeout',
  resolve_failed: 'tiktok_resolve_failed',
  invalid_url: 'tiktok_invalid_url'
};
export interface TikTokResolveResult {
  url: string;
  duration: number;
  cover: string;
  title: string;
  author: string;
  size: number;
}
interface TikTokLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onResolved: (result: TikTokResolveResult) => void;
  onAuthRequired?: () => void;
  /** Pre-fill URL and auto-trigger import when dialog opens */
  initialUrl?: string;
}
export function TikTokLinkDialog({
  open,
  onOpenChange,
  onResolved,
  onAuthRequired,
  initialUrl
}: TikTokLinkDialogProps) {
  const t = useTranslations('common.uploader');
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const autoTriggeredRef = useRef(false);

  // Auto-fill and trigger import when dialog opens with initialUrl
  useEffect(() => {
    if (open && initialUrl && !autoTriggeredRef.current) {
      autoTriggeredRef.current = true;
      setUrl(initialUrl);
      handleImport(initialUrl);
    }
    if (!open) {
      autoTriggeredRef.current = false;
    }
  }, [open, initialUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImport = async (overrideUrl?: string) => {
    const trimmed = (overrideUrl ?? url).trim();
    if (!trimmed || !TIKTOK_URL_REGEX.test(trimmed)) {
      setError(t('tiktok_invalid_url'));
      return;
    }
    setUrl(trimmed);
    setError('');
    setIsLoading(true);
    try {
      const resp = await fetch('/api/tiktok/resolve', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          url: trimmed
        })
      });
      const json = await resp.json().catch(() => null);
      if (!resp.ok || !json) {
        const key = json?.message && TIKTOK_ERROR_KEYS[json.message];
        setError(key ? t(key) : t('tiktok_resolve_failed'));
        return;
      }
      const {
        code,
        message,
        data
      } = json;
      if (code !== 0) {
        if (message?.includes('no auth')) {
          onOpenChange(false);
          setUrl('');
          toast.error(t('tiktok_auth_required'));
          onAuthRequired?.();
          return;
        }
        const key_0 = message && TIKTOK_ERROR_KEYS[message];
        setError(key_0 ? t(key_0) : t('tiktok_resolve_failed'));
        return;
      }
      onResolved(data as TikTokResolveResult);
      onOpenChange(false);
      setUrl('');
    } catch (err: any) {
      console.error('TikTok resolve failed:', err);
      setError(t('tiktok_resolve_failed'));
    } finally {
      setIsLoading(false);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleImport();
    }
  };
  return <Dialog open={open} onOpenChange={v => {
    if (!isLoading) {
      onOpenChange(v);
      if (!v) {
        setUrl('');
        setError('');
      }
    }
  }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LinkIcon className="h-4 w-4" />
            {t('tiktok_dialog_title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-muted-foreground text-sm">
            {t('tiktok_dialog_description')}
          </p>

          <div className="flex gap-2">
            <Input value={url} onChange={e_0 => {
            setUrl(e_0.target.value);
            if (error) setError('');
          }} onKeyDown={handleKeyDown} placeholder={t('tiktok_dialog_placeholder')} disabled={isLoading} autoFocus />
            <Button onClick={() => handleImport()} disabled={isLoading || !url.trim()} className="shrink-0">
              {isLoading ? <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('tiktok_dialog_importing')}
                </> : t('tiktok_dialog_import')}
            </Button>
          </div>

          {error && <p className="text-destructive text-sm">{error}</p>}

          <p className="text-muted-foreground text-xs">
            {t('tiktok_dialog_supported_formats')}
          </p>

          <div className="bg-accent/20 rounded-md p-3 text-xs">
            <p className="text-muted-foreground">
              {t('tiktok_dialog_extension_tip')}{' '}
              <Link href="/tools/ai-dance-generator-helper-for-tiktok" target="_blank" className="text-primary font-medium underline underline-offset-2 hover:no-underline">
                {t('tiktok_dialog_extension_link')}
              </Link>
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>;
}